import {
  ContextMenuShowingEventArgs,
  CustomCommandExecutedEventArgs,
  RichEdit,
} from 'devexpress-richedit';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { DXR } from 'components/devexpress/RichEdit/dx-richedit';
import {
  BaseRibbonItem,
  ContextMenuUiItem,
  isButtonItem,
  isDropDownItem,
  isRibbonItem,
  UiItemConfig,
} from './types';
import { RichEditUiRegistryContext } from './RichEditUiRegistryContext';

type Props = {
  richEdit: React.RefObject<RichEdit>;
};

export const RichEditUiProvider: React.FC<React.PropsWithChildren<Props>> = ({
  richEdit,
  children,
}) => {
  const ribbonItems = useRef<Map<string, BaseRibbonItem>>(new Map());
  const contextItems = useRef<Map<string, ContextMenuUiItem>>(new Map());

  const updatedItems = useRef<Set<string>>(new Set());

  const [value, forceUpdate] = useReducer((x) => x + 1, 0);

  const register = useCallback((item: UiItemConfig) => {
    if (ribbonItems.current.has(item.id) || contextItems.current.has(item.id))
      return;

    updatedItems.current.add(item.id);

    if (isRibbonItem(item)) {
      ribbonItems.current.set(item.id, item);
    } else {
      contextItems.current.set(item.id, item);
    }
    forceUpdate();
  }, []);

  const unregister = useCallback((key: string) => {
    if (!ribbonItems.current.has(key) && !contextItems.current.has(key)) return;

    ribbonItems.current.delete(key);
    contextItems.current.delete(key);
    forceUpdate();
  }, []);

  useEffect(() => {
    if (!richEdit.current) return;

    const editor = richEdit.current;

    editor.updateRibbon((ribbon) => {
      ribbonItems.current.forEach((item) => {
        if (!updatedItems.current.has(item.id)) return;

        const tab = ribbon.getTab(item.tab);
        if (!tab) return;

        if (isButtonItem(item) && item.enabled) {
          if (tab.getItem(item.id)) tab.removeItem(item.id);

          tab.insertItem(
            new DXR.RibbonButtonItem(item.id, item.text, {
              icon: item.icon,
              showText: item.showText ?? true,
              toggleMode: item.toggleMode,
            }),
            item.insertIndex,
          );
        }

        if (isDropDownItem(item) && item.enabled) {
          if (tab.getItem(item.id)) tab.removeItem(item.id);

          const subItems = item.items.map(
            (i) =>
              new DXR.RibbonSubMenuItem(i.id, i.text, undefined, {
                icon: i.icon,
              }),
          );

          tab.insertItem(
            new DXR.RibbonMenuItem(item.id, item.text, subItems, {
              icon: item.icon,
              showText: item.showText,
            }),
            item.insertIndex,
          );
        }

        updatedItems.current.delete(item.id);
      });
    });

    contextItems.current.forEach((item) => {
      if (!updatedItems.current.has(item.id)) return;

      if (editor.contextMenu.getItem(item.id))
        editor.contextMenu.removeItem(item.id);

      editor.contextMenu.insertItem(
        new DXR.ContextMenuItem(item.id, {
          icon: item.icon,
          text: item.text,
          beginGroup: item.beginGroup,
        }),
        item.insertIndex,
      );
    });

    const commandHandler = (
      sender?: RichEdit,
      args?: CustomCommandExecutedEventArgs,
    ) => {
      if (!sender || !args) return;

      ribbonItems.current.forEach((item, key) => {
        if (key === args.commandName) {
          item.action(sender, args);
        }

        if (
          isDropDownItem(item) &&
          item.items.some((i) => i.id === args.commandName)
        ) {
          item.action(sender, args);
        }
      });

      const contextItem = contextItems.current.get(args.commandName);
      if (contextItem) {
        contextItem.action(sender, args);
      }
    };

    const contextShowingHandler = (
      sender?: RichEdit,
      args?: ContextMenuShowingEventArgs,
    ) => {
      if (!sender || !args) return;

      contextItems.current.forEach((item) => {
        item.onContextMenuShowing(sender, args);
      });
    };

    editor.events.customCommandExecuted.addHandler(commandHandler);
    editor.events.contextMenuShowing.addHandler(contextShowingHandler);

    return () => {
      editor.events.customCommandExecuted.removeHandler(commandHandler);
      editor.events.contextMenuShowing.removeHandler(contextShowingHandler);
    };
  }, [value, richEdit]);

  return (
    <RichEditUiRegistryContext.Provider
      value={{
        register,
        unregister,
      }}
    >
      {children}
    </RichEditUiRegistryContext.Provider>
  );
};
